import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AuthCallback from "./components/auth/AuthCallback";
import MaintenanceGate from "./components/MaintenanceGate";
import CookieConsent from "./components/CookieConsent";
import ErrorBoundary from "./components/ErrorBoundary";
import { loadBranding } from "./lib/useBranding";

// Public/prerendered pages are eagerly imported so their prerendered HTML hydrates
// cleanly (React.lazy would show a Suspense fallback on first client render -> mismatch).
import Home from "./pages/Home";
import Categories from "./pages/Categories";
import CategoryLanding from "./pages/CategoryLanding";
import LocalLanding from "./pages/LocalLanding";
import Spotlight from "./pages/Spotlight";
import SpotlightArticle from "./pages/SpotlightArticle";
import AdminSpotlight from "./pages/admin/AdminSpotlight";
import SignIn from "./pages/SignIn";
import HowItWorks from "./pages/public/HowItWorks";
import ForOrganisers from "./pages/public/ForOrganisers";
import ForTalentsPage from "./pages/public/ForTalents";
import JoinPage from "./pages/public/Join";
import About from "./pages/public/About";
import Contact from "./pages/public/Contact";
import FAQ from "./pages/public/FAQ";
import Terms from "./pages/public/Terms";
import Privacy from "./pages/public/Privacy";
import Pricing from "./pages/public/Pricing";

// Auth-gated / non-prerendered pages are lazy chunks (biggest bundle-size win).
// Each lazy page carries its OWN Suspense so prerendered (eager) routes never have a
// Suspense boundary in their tree -> clean React 19 hydration of the prerendered HTML.
const lazyPage = (importFn) => {
  const C = lazy(importFn);
  const Wrapped = (props) => (
    <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
      <C {...props} />
    </Suspense>
  );
  return Wrapped;
};

const OAuthCallback = lazyPage(() => import("./pages/OAuthCallback"));
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));
const VerifyEmail = lazyPage(() => import("./pages/VerifyEmail"));
const RoleSelect = lazyPage(() => import("./pages/RoleSelect"));
const BookingRequest = lazyPage(() => import("./pages/BookingRequest"));
const TalentDirectory = lazyPage(() => import("./pages/TalentDirectory"));
const BookingStatus = lazyPage(() => import("./pages/BookingStatus"));
const BookingVerify = lazyPage(() => import("./pages/BookingVerify"));
const DashboardRouter = lazyPage(() => import("./pages/DashboardRouter"));
const AgencyRoster = lazyPage(() => import("./pages/agency/AgencyRoster"));
const AgencySettings = lazyPage(() => import("./pages/agency/AgencySettings"));

const OffersHub = lazyPage(() => import("./pages/client/OffersHub"));
const ClientBookings = lazyPage(() => import("./pages/client/ClientBookings"));
const ClientEvents = lazyPage(() => import("./pages/client/ClientEvents"));
const Notifications = lazyPage(() => import("./pages/Notifications"));
const ClientEventDetail = lazyPage(() => import("./pages/client/ClientEventDetail"));
const ClientTalents = lazyPage(() => import("./pages/client/ClientTalents"));
const ClientSettings = lazyPage(() => import("./pages/client/ClientSettings"));
const ClientInvoices = lazyPage(() => import("./pages/client/ClientInvoices"));
const ClientInvoicePay = lazyPage(() => import("./pages/client/ClientInvoicePay"));
const ClientBilling = lazyPage(() => import("./pages/client/ClientBilling"));

const TalentGigs = lazyPage(() => import("./pages/talent/TalentGigs"));
const TalentGigDetail = lazyPage(() => import("./pages/talent/TalentGigDetail"));
const TalentOffer = lazyPage(() => import("./pages/talent/TalentOffer"));
const TalentOffers = lazyPage(() => import("./pages/talent/TalentOffers"));
const TalentSettings = lazyPage(() => import("./pages/talent/TalentSettings"));
const TalentPaymentSettings = lazyPage(() => import("./pages/talent/TalentPaymentSettings"));
const TalentTaxSettings = lazyPage(() => import("./pages/talent/TalentTaxSettings"));
const TalentEarnings = lazyPage(() => import("./pages/talent/TalentEarnings"));
const BookingDetail = lazyPage(() => import("./pages/BookingDetail"));
const TalentInvoiceSettings = lazyPage(() => import("./pages/talent/TalentInvoiceSettings"));
// New settings sub-pages (matrix of notif channels + security + sessions + GDPR + export + prefs + danger).
const TalentNotificationsSettings = lazyPage(() => import("./pages/talent/TalentSettingsSections").then(m => ({ default: m.TalentNotificationsSettings })));
const TalentSecuritySettings = lazyPage(() => import("./pages/talent/TalentSettingsSections").then(m => ({ default: m.TalentSecuritySettings })));
const TalentSessionsSettings = lazyPage(() => import("./pages/talent/TalentSettingsSections").then(m => ({ default: m.TalentSessionsSettings })));
const TalentPrivacySettings = lazyPage(() => import("./pages/talent/TalentSettingsSections").then(m => ({ default: m.TalentPrivacySettings })));
const TalentExportSettings = lazyPage(() => import("./pages/talent/TalentSettingsSections").then(m => ({ default: m.TalentExportSettings })));
const TalentPreferencesSettings = lazyPage(() => import("./pages/talent/TalentSettingsSections").then(m => ({ default: m.TalentPreferencesSettings })));
const TalentDangerZone = lazyPage(() => import("./pages/talent/TalentSettingsSections").then(m => ({ default: m.TalentDangerZone })));
const TalentAddons = lazyPage(() => import("./pages/talent/TalentAddons"));
const TalentPricing = lazyPage(() => import("./pages/talent/TalentPricing"));
const TalentMedia = lazyPage(() => import("./pages/talent/TalentMedia"));
const TalentNewRequest = lazyPage(() => import("./pages/talent/TalentNewRequest"));
const ErrorBoundaryQa = lazyPage(() => import("./pages/_qa/ErrorBoundaryQa"));
const TalentInvoices = lazyPage(() => import("./pages/talent/TalentInvoices"));
const TalentInvoiceDetail = lazyPage(() => import("./pages/talent/TalentInvoiceDetail"));
const TalentCustomers = lazyPage(() => import("./pages/talent/TalentCustomers"));
const CheckoutPage = lazyPage(() => import("./pages/client/CheckoutPage"));
const TalentBookings = lazyPage(() => import("./pages/talent/TalentBookings"));
const TalentAvailability = lazyPage(() => import("./pages/talent/TalentAvailability"));
const Credits = lazyPage(() => import("./pages/talent/Credits"));
const CreditHistory = lazyPage(() => import("./pages/talent/CreditHistory"));

const AdminTalents = lazyPage(() => import("./pages/admin/AdminTalents"));
const AdminInvoices = lazyPage(() => import("./pages/admin/AdminInvoices"));
const AdminLeads = lazyPage(() => import("./pages/admin/AdminLeads"));
const AdminAttention = lazyPage(() => import("./pages/admin/AdminAttention"));
const AdminReminders = lazyPage(() => import("./pages/admin/AdminReminders"));
const TalentOnboarding = lazyPage(() => import("./pages/talent/TalentOnboarding"));
const AdminSecurity = lazyPage(() => import("./pages/admin/AdminSecurity"));
const AdminRefunds = lazyPage(() => import("./pages/admin/AdminRefunds"));
const AdminVerification = lazyPage(() => import("./pages/admin/AdminVerification"));
const AdminDisputes = lazyPage(() => import("./pages/admin/AdminDisputes"));
const AdminUsers = lazyPage(() => import("./pages/admin/AdminUsers"));
const AdminSettings = lazyPage(() => import("./pages/admin/AdminSettings"));
const AdminAIInsights = lazyPage(() => import("./pages/AdminAIInsights"));
const AdminSEO = lazyPage(() => import("./pages/admin/AdminSEO"));
const AdminAds = lazyPage(() => import("./pages/admin/AdminAds"));
const AdminPricing = lazyPage(() => import("./pages/admin/AdminPricing"));
const AdminMedia = lazyPage(() => import("./pages/admin/AdminMedia"));
const AdminMessages = lazyPage(() => import("./pages/admin/AdminMessages"));
const AdminReviews = lazyPage(() => import("./pages/admin/AdminReviews"));
const AdminEmailLogs = lazyPage(() => import("./pages/admin/AdminEmailLogs"));
const AdminErrorReports = lazyPage(() => import("./pages/admin/AdminErrorReports"));
const AdminSupport = lazyPage(() => import("./pages/admin/AdminSupport"));
const AdminArchive = lazyPage(() => import("./pages/admin/AdminArchive"));
const AdminDataIntegrity = lazyPage(() => import("./pages/admin/AdminDataIntegrity"));
const AdminDac7 = lazyPage(() => import("./pages/admin/AdminDac7"));
const AdminRoles = lazyPage(() => import("./pages/admin/AdminRoles"));
const AdminInvitations = lazyPage(() => import("./pages/admin/AdminInvitations"));
const Unsubscribe = lazyPage(() => import("./pages/Unsubscribe"));
const AdminActivityLog = lazyPage(() => import("./pages/admin/AdminActivityLog"));
const AdminTranslations = lazyPage(() => import("./pages/admin/AdminTranslations"));
const AdminCms = lazyPage(() => import("./pages/admin/AdminCms"));
const AdminEmailTemplates = lazyPage(() => import("./pages/admin/AdminEmailTemplates"));
const AdminOffers = lazyPage(() => import("./pages/admin/AdminOffers"));
const AdminBookings = lazyPage(() => import("./pages/admin/AdminBookings"));
const AdminPayments = lazyPage(() => import("./pages/admin/AdminPayments"));
const AdminInvoicing = lazyPage(() => import("./pages/admin/AdminInvoicing"));
const AdminCommissions = lazyPage(() => import("./pages/admin/AdminCommissions"));
const Support = lazyPage(() => import("./pages/Support"));

const Messages = lazyPage(() => import("./pages/Messages"));
const TalentPublicProfile = lazyPage(() => import("./pages/TalentPublicProfile"));

const AccountPrivacy = lazyPage(() => import("./pages/AccountPrivacy"));
const ReferralPage = lazyPage(() => import("./pages/ReferralPage"));
const AdminModeration = lazyPage(() => import("./pages/admin/AdminModeration"));
const PageNotFound = lazyPage(() => import("./lib/PageNotFound"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Legacy /boka/:slug booking-category links redirect to the Swedish SEO route.
const BokaRedirect = () => {
  const { slug, city } = useParams();
  return <Navigate to={`/sv/boka/${slug}${city ? `/${city}` : ""}`} replace />;
};

function AppRouter() {
  const location = useLocation();
  useEffect(() => { loadBranding(); }, []);
  // Handle Emergent Google OAuth redirect (#session_id=...) before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <MaintenanceGate>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Home />} />
        <Route path="/sv" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/priser" element={<Pricing />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/sv/how-it-works" element={<HowItWorks />} />
        <Route path="/for-organisers" element={<Navigate to="/how-it-works" replace />} />
        <Route path="/sv/for-organisers" element={<Navigate to="/sv/how-it-works" replace />} />
        <Route path="/for-talents" element={<ForTalentsPage />} />
        <Route path="/sv/for-talents" element={<ForTalentsPage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/sv/join" element={<JoinPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/sv/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/sv/contact" element={<Contact />} />
        <Route path="/sv/kontakt" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/sv/faq" element={<FAQ />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/sv/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/sv/privacy" element={<Privacy />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/sv/boka" element={<Categories />} />
        <Route path="/book/:category" element={<CategoryLanding />} />
        <Route path="/book/:category/:city" element={<CategoryLanding />} />
        <Route path="/sv/boka/:category" element={<CategoryLanding />} />
        <Route path="/sv/boka/:category/:city" element={<CategoryLanding />} />

        {/* Hyper-local Stockholm/Göteborg SEO landing pages */}
        <Route path="/sv/boka-dj-stockholm" element={<LocalLanding pageKey="boka-dj-stockholm" />} />
        <Route path="/book-dj-stockholm" element={<LocalLanding pageKey="boka-dj-stockholm" />} />
        <Route path="/sv/boka-dj-goteborg" element={<LocalLanding pageKey="boka-dj-goteborg" />} />
        <Route path="/book-dj-gothenburg" element={<LocalLanding pageKey="boka-dj-goteborg" />} />
        <Route path="/sv/livemusik-brollop-stockholm" element={<LocalLanding pageKey="livemusik-brollop-stockholm" />} />
        <Route path="/live-music-wedding-stockholm" element={<LocalLanding pageKey="livemusik-brollop-stockholm" />} />
        <Route path="/sv/livemusik-brollop-goteborg" element={<LocalLanding pageKey="livemusik-brollop-goteborg" />} />
        <Route path="/live-music-wedding-gothenburg" element={<LocalLanding pageKey="livemusik-brollop-goteborg" />} />
        <Route path="/sv/underhallning-foretagsevent-stockholm" element={<LocalLanding pageKey="underhallning-foretagsevent-stockholm" />} />
        <Route path="/corporate-event-entertainment-stockholm" element={<LocalLanding pageKey="underhallning-foretagsevent-stockholm" />} />
        <Route path="/sv/underhallning-foretagsevent-goteborg" element={<LocalLanding pageKey="underhallning-foretagsevent-goteborg" />} />
        <Route path="/corporate-event-entertainment-gothenburg" element={<LocalLanding pageKey="underhallning-foretagsevent-goteborg" />} />
        <Route path="/sv/hyr-artist-stockholm" element={<LocalLanding pageKey="hyr-artist-stockholm" />} />
        <Route path="/hire-artist-stockholm" element={<LocalLanding pageKey="hyr-artist-stockholm" />} />
        <Route path="/sv/hyr-artist-goteborg" element={<LocalLanding pageKey="hyr-artist-goteborg" />} />
        <Route path="/hire-artist-gothenburg" element={<LocalLanding pageKey="hyr-artist-goteborg" />} />
        <Route path="/sv/eventunderhallning-stockholm" element={<LocalLanding pageKey="eventunderhallning-stockholm" />} />
        <Route path="/event-entertainment-stockholm" element={<LocalLanding pageKey="eventunderhallning-stockholm" />} />
        <Route path="/sv/livebands-brollop-stockholm" element={<LocalLanding pageKey="livebands-brollop-stockholm" />} />
        <Route path="/live-bands-for-weddings-stockholm" element={<LocalLanding pageKey="livebands-brollop-stockholm" />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/login" element={<Navigate to="/signin" replace />} />
        <Route path="/register" element={<Navigate to="/signin" replace />} />
        <Route path="/signup" element={<Navigate to="/signin" replace />} />
        <Route path="/auth" element={<Navigate to="/signin" replace />} />
        <Route path="/role" element={<ProtectedRoute><RoleSelect /></ProtectedRoute>} />
        <Route path="/talents" element={<ProtectedRoute><TalentDirectory /></ProtectedRoute>} />
        <Route path="/talent/:id" element={<TalentPublicProfile />} />
        <Route path="/sv/talent/:id" element={<TalentPublicProfile />} />
        <Route path="/boka" element={<BookingRequest />} />
        <Route path="/boka/:slug" element={<BokaRedirect />} />
        <Route path="/boka/:slug/:city" element={<BokaRedirect />} />
        <Route path="/book" element={<BookingRequest />} />
        <Route path="/BookingRequest" element={<BookingRequest />} />
        <Route path="/BookingStatus" element={<BookingStatus />} />
        <Route path="/CheckBookingStatus" element={<BookingStatus />} />
        <Route path="/booking-verify" element={<BookingVerify />} />
        <Route path="/t/:id" element={<TalentPublicProfile />} />
        <Route path="/TalentPublicProfile/:id" element={<TalentPublicProfile />} />

        {/* Dashboard (role router) */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />

        {/* Client */}
        <Route path="/offers" element={<ProtectedRoute roles={["client", "talent", "agency"]}><OffersHub /></ProtectedRoute>} />
        <Route path="/client/talents" element={<ProtectedRoute roles={["client"]}><ClientTalents /></ProtectedRoute>} />
        <Route path="/client/bookings" element={<ProtectedRoute roles={["client"]}><ClientBookings /></ProtectedRoute>} />
        {/* Eventvyn ur designen 5 aug. Den gamla bokningslistan ligger kvar tills
            den nya är verifierad: en redirect kostar en rad och räddar varje
            bokmärke och varje mejl som redan gått ut. */}
        <Route path="/client/events" element={<ProtectedRoute roles={["client"]}><ClientEvents /></ProtectedRoute>} />
        {/* Notiscentret. Delas av alla roller: kategorierna skiljer sig men
            listan gör inte det. */}
        <Route path="/notifications" element={<ProtectedRoute roles={["client", "talent", "agency", "admin"]}><Notifications /></ProtectedRoute>} />
        <Route path="/client/events/:id" element={<ProtectedRoute roles={["client"]}><ClientEventDetail /></ProtectedRoute>} />
        <Route path="/client/invoices" element={<ProtectedRoute roles={["client"]}><ClientInvoices /></ProtectedRoute>} />
        <Route path="/sv/client/invoices" element={<ProtectedRoute roles={["client"]}><ClientInvoices /></ProtectedRoute>} />
        <Route path="/client/invoices/:id/pay" element={<ProtectedRoute roles={["client"]}><ClientInvoicePay /></ProtectedRoute>} />
        <Route path="/client/billing" element={<ProtectedRoute roles={["client"]}><ClientBilling /></ProtectedRoute>} />
        <Route path="/client/settings" element={<ProtectedRoute roles={["client"]}><ClientSettings /></ProtectedRoute>} />

        {/* Messaging (client + talent + agency) */}
        <Route path="/messages" element={<ProtectedRoute roles={["client", "talent", "agency"]}><Messages /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute roles={["client", "talent", "agency"]}><Support /></ProtectedRoute>} />
        <Route path="/account/privacy" element={<ProtectedRoute roles={["client", "talent", "agency", "admin"]}><AccountPrivacy /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute roles={["talent", "agency"]}><ReferralPage /></ProtectedRoute>} />

        {/* Talent + Agency */}
        <Route path="/agency/roster" element={<ProtectedRoute roles={["agency"]}><AgencyRoster /></ProtectedRoute>} />
        <Route path="/agency/settings" element={<ProtectedRoute roles={["agency"]}><AgencySettings /></ProtectedRoute>} />
        <Route path="/talent/onboarding" element={<ProtectedRoute roles={["talent", "agency"]}><TalentOnboarding /></ProtectedRoute>} />
        <Route path="/talent/gigs" element={<ProtectedRoute roles={["talent", "agency"]}><TalentGigs /></ProtectedRoute>} />
        {/* Detaljvyn tar både /:id och /:id/offer. Samma sida, offertmodalen öppen i det
            andra fallet, så att länken går att dela och bakåtknappen fungerar. */}
        <Route path="/talent/gigs/:id" element={<ProtectedRoute roles={["talent", "agency"]}><TalentGigDetail /></ProtectedRoute>} />
        <Route path="/talent/gigs/:id/offer" element={<ProtectedRoute roles={["talent", "agency"]}><TalentOffer /></ProtectedRoute>} />
        <Route path="/sv/talent/gigs/:id" element={<ProtectedRoute roles={["talent", "agency"]}><TalentGigDetail /></ProtectedRoute>} />
        <Route path="/sv/talent/gigs/:id/offer" element={<ProtectedRoute roles={["talent", "agency"]}><TalentGigDetail /></ProtectedRoute>} />
        <Route path="/talent/offers" element={<ProtectedRoute roles={["talent", "agency"]}><TalentOffers /></ProtectedRoute>} />
        <Route path="/talent/settings" element={<ProtectedRoute roles={["talent", "agency"]}><TalentSettings /></ProtectedRoute>} />
        <Route path="/talent/settings/payments" element={<ProtectedRoute roles={["talent", "agency"]}><TalentPaymentSettings /></ProtectedRoute>} />
        <Route path="/talent/earnings" element={<ProtectedRoute roles={["talent", "agency"]}><TalentEarnings /></ProtectedRoute>} />
        {/* Samma sida för båda parter. Backend avgör vad som får synas, så en
            kund som öppnar talangens adress får kundvyn, inte ett fel. */}
        <Route path="/talent/bookings/:id" element={<ProtectedRoute roles={["talent", "agency", "admin"]}><BookingDetail /></ProtectedRoute>} />
        <Route path="/client/bookings/:id" element={<ProtectedRoute roles={["client", "talent", "agency", "admin"]}><BookingDetail /></ProtectedRoute>} />
        <Route path="/sv/talent/bookings/:id" element={<ProtectedRoute roles={["talent", "agency", "admin"]}><BookingDetail /></ProtectedRoute>} />
        <Route path="/sv/client/bookings/:id" element={<ProtectedRoute roles={["client", "talent", "agency", "admin"]}><BookingDetail /></ProtectedRoute>} />
        <Route path="/sv/talent/earnings" element={<ProtectedRoute roles={["talent", "agency"]}><TalentEarnings /></ProtectedRoute>} />
        {/* Designdokumenten skrev /dashboard/earnings. Kodens namnrymd är /talent/*,
            så den gamla adressen leder hit i stället för att bli en död länk. */}
        <Route path="/dashboard/earnings" element={<Navigate to="/talent/earnings" replace />} />
        <Route path="/talent/settings/tax" element={<ProtectedRoute roles={["talent", "agency"]}><TalentTaxSettings /></ProtectedRoute>} />
        <Route path="/sv/talent/settings/tax" element={<ProtectedRoute roles={["talent", "agency"]}><TalentTaxSettings /></ProtectedRoute>} />
        <Route path="/talent/new-request" element={<ProtectedRoute roles={["talent", "agency", "client"]}><TalentNewRequest /></ProtectedRoute>} />
        <Route path="/dashboard/new-request" element={<ProtectedRoute roles={["talent", "agency", "client"]}><TalentNewRequest /></ProtectedRoute>} />
        <Route path="/_qa/error-boundary" element={<ProtectedRoute roles={["talent", "agency", "client", "admin"]}><ErrorBoundaryQa /></ProtectedRoute>} />
        <Route path="/talent/invoices" element={<ProtectedRoute roles={["talent", "agency"]}><TalentInvoices /></ProtectedRoute>} />
        <Route path="/talent/invoices/:id" element={<ProtectedRoute roles={["talent", "agency"]}><TalentInvoiceDetail /></ProtectedRoute>} />
        <Route path="/talent/invoices/:id/preview" element={<ProtectedRoute roles={["talent", "agency"]}><TalentInvoiceDetail /></ProtectedRoute>} />
        <Route path="/sv/talent/invoices/:id" element={<ProtectedRoute roles={["talent", "agency"]}><TalentInvoiceDetail /></ProtectedRoute>} />
        <Route path="/talent/customers" element={<ProtectedRoute roles={["talent", "agency"]}><TalentCustomers /></ProtectedRoute>} />
        <Route path="/talent/settings/invoice" element={<ProtectedRoute roles={["talent", "agency"]}><TalentInvoiceSettings /></ProtectedRoute>} />
        <Route path="/sv/talent/settings/invoice" element={<ProtectedRoute roles={["talent", "agency"]}><TalentInvoiceSettings /></ProtectedRoute>} />
        {/* New unified settings sub-pages — reachable from SettingsSidebar. */}
        <Route path="/talent/settings/notifications" element={<ProtectedRoute roles={["talent", "agency"]}><TalentNotificationsSettings /></ProtectedRoute>} />
        <Route path="/talent/settings/security" element={<ProtectedRoute roles={["talent", "agency"]}><TalentSecuritySettings /></ProtectedRoute>} />
        <Route path="/talent/settings/sessions" element={<ProtectedRoute roles={["talent", "agency"]}><TalentSessionsSettings /></ProtectedRoute>} />
        <Route path="/talent/settings/privacy" element={<ProtectedRoute roles={["talent", "agency"]}><TalentPrivacySettings /></ProtectedRoute>} />
        <Route path="/talent/settings/export" element={<ProtectedRoute roles={["talent", "agency"]}><TalentExportSettings /></ProtectedRoute>} />
        <Route path="/talent/settings/preferences" element={<ProtectedRoute roles={["talent", "agency"]}><TalentPreferencesSettings /></ProtectedRoute>} />
        <Route path="/talent/settings/danger" element={<ProtectedRoute roles={["talent", "agency"]}><TalentDangerZone /></ProtectedRoute>} />
        {/* Legacy alias — /talent/settings/account maps to the profile editor for continuity. */}
        <Route path="/talent/settings/account" element={<Navigate to="/talent/settings" replace />} />
        <Route path="/talent/settings/addons" element={<ProtectedRoute roles={["talent", "agency"]}><TalentAddons /></ProtectedRoute>} />
        <Route path="/talent/media" element={<ProtectedRoute roles={["talent", "agency"]}><TalentMedia /></ProtectedRoute>} />
        <Route path="/talent/pricing" element={<ProtectedRoute roles={["talent", "agency"]}><TalentPricing /></ProtectedRoute>} />
        <Route path="/sv/talent/pricing" element={<ProtectedRoute roles={["talent", "agency"]}><TalentPricing /></ProtectedRoute>} />
        <Route path="/sv/talent/settings/addons" element={<ProtectedRoute roles={["talent", "agency"]}><TalentAddons /></ProtectedRoute>} />
        <Route path="/offers/:id/checkout" element={<ProtectedRoute roles={["client"]}><CheckoutPage /></ProtectedRoute>} />
        <Route path="/talent/bookings" element={<ProtectedRoute roles={["talent", "agency"]}><TalentBookings /></ProtectedRoute>} />
        <Route path="/talent/availability" element={<ProtectedRoute roles={["talent", "agency"]}><TalentAvailability /></ProtectedRoute>} />
        <Route path="/credits" element={<ProtectedRoute roles={["talent", "agency"]}><Credits /></ProtectedRoute>} />
        <Route path="/credits/history" element={<ProtectedRoute roles={["talent", "agency"]}><CreditHistory /></ProtectedRoute>} />

        {/* Admin */}
        {/* Attention-kön. Tio köer som i dag bor på fjorton sidor, sorterade
            efter hur länge de väntat i förhållande till sitt löfte. */}
        <Route path="/admin/attention" element={<ProtectedRoute roles={["admin"]}><AdminAttention /></ProtectedRoute>} />
        <Route path="/admin/reminders" element={<ProtectedRoute roles={["admin"]}><AdminReminders /></ProtectedRoute>} />
        <Route path="/admin/security" element={<ProtectedRoute roles={["admin"]}><AdminSecurity /></ProtectedRoute>} />
        <Route path="/admin/talents" element={<ProtectedRoute roles={["admin"]}><AdminTalents /></ProtectedRoute>} />
        <Route path="/admin/verification" element={<ProtectedRoute roles={["admin"]}><AdminVerification /></ProtectedRoute>} />
        <Route path="/admin/refunds" element={<ProtectedRoute roles={["admin"]}><AdminRefunds /></ProtectedRoute>} />
        <Route path="/admin/disputes" element={<ProtectedRoute roles={["admin"]}><AdminDisputes /></ProtectedRoute>} />
        <Route path="/admin/invoices" element={<ProtectedRoute roles={["admin"]}><AdminInvoices /></ProtectedRoute>} />
        <Route path="/admin/leads" element={<ProtectedRoute roles={["admin"]}><AdminLeads /></ProtectedRoute>} />
        <Route path="/admin/reviews" element={<ProtectedRoute roles={["admin"]}><AdminReviews /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminSettings /></ProtectedRoute>} />
        <Route path="/AdminAIInsights" element={<ProtectedRoute roles={["admin"]}><AdminAIInsights /></ProtectedRoute>} />
        <Route path="/admin/pricing" element={<ProtectedRoute roles={["admin"]}><AdminPricing /></ProtectedRoute>} />
        <Route path="/admin/media" element={<ProtectedRoute roles={["admin"]}><AdminMedia /></ProtectedRoute>} />
        <Route path="/admin/messages" element={<ProtectedRoute roles={["admin"]}><AdminMessages /></ProtectedRoute>} />
        <Route path="/admin/email-logs" element={<ProtectedRoute roles={["admin"]}><AdminEmailLogs /></ProtectedRoute>} />
        <Route path="/admin/error-reports" element={<ProtectedRoute roles={["admin"]}><AdminErrorReports /></ProtectedRoute>} />
        <Route path="/admin/spotlight" element={<ProtectedRoute roles={["admin"]}><AdminSpotlight /></ProtectedRoute>} />
        <Route path="/admin/seo" element={<ProtectedRoute roles={["admin"]}><AdminSEO /></ProtectedRoute>} />
        <Route path="/admin/ads" element={<ProtectedRoute roles={["admin"]}><AdminAds /></ProtectedRoute>} />

        {/* Public Spotlight (editorial) */}
        <Route path="/spotlight" element={<Spotlight />} />
        <Route path="/sv/spotlight" element={<Spotlight />} />
        <Route path="/spotlight/:slug" element={<SpotlightArticle />} />
        <Route path="/sv/spotlight/:slug" element={<SpotlightArticle />} />
        <Route path="/admin/support" element={<ProtectedRoute roles={["admin"]}><AdminSupport /></ProtectedRoute>} />
        <Route path="/admin/archive" element={<ProtectedRoute roles={["admin"]}><AdminArchive /></ProtectedRoute>} />
        <Route path="/admin/data-integrity" element={<ProtectedRoute roles={["admin"]}><AdminDataIntegrity /></ProtectedRoute>} />
        <Route path="/admin/compliance/dac7" element={<ProtectedRoute roles={["admin"]}><AdminDac7 /></ProtectedRoute>} />
        <Route path="/admin/settings/roles" element={<ProtectedRoute roles={["admin"]}><AdminRoles /></ProtectedRoute>} />
        <Route path="/admin/invitations" element={<ProtectedRoute roles={["admin"]}><AdminInvitations /></ProtectedRoute>} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/admin/activity" element={<ProtectedRoute roles={["admin"]}><AdminActivityLog /></ProtectedRoute>} />
        <Route path="/admin/moderation" element={<ProtectedRoute roles={["admin"]}><AdminModeration /></ProtectedRoute>} />
        <Route path="/admin/translations" element={<ProtectedRoute roles={["admin"]}><AdminTranslations /></ProtectedRoute>} />
        <Route path="/admin/cms" element={<ProtectedRoute roles={["admin"]}><AdminCms /></ProtectedRoute>} />
        <Route path="/admin/email-templates" element={<ProtectedRoute roles={["admin"]}><AdminEmailTemplates /></ProtectedRoute>} />
        <Route path="/admin/offers" element={<ProtectedRoute roles={["admin"]}><AdminOffers /></ProtectedRoute>} />
        <Route path="/admin/bookings" element={<ProtectedRoute roles={["admin"]}><AdminBookings /></ProtectedRoute>} />
        <Route path="/admin/payments" element={<ProtectedRoute roles={["admin"]}><AdminPayments /></ProtectedRoute>} />
        <Route path="/admin/invoicing" element={<ProtectedRoute roles={["admin"]}><AdminInvoicing /></ProtectedRoute>} />
        <Route path="/admin/commissions" element={<ProtectedRoute roles={["admin"]}><AdminCommissions /></ProtectedRoute>} />

        {/* Legacy / malformed Swedish booking wildcards fall back to the category picker */}
        <Route path="/sv/boka/*" element={<Navigate to="/sv/boka" replace />} />

        {/* Catch-all branded 404 (must remain last) */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
      </MaintenanceGate>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
            <CookieConsent />
            <Toaster />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
